import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Radio,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import FileUpload from "./Components/FileUpload";
import InterviewRound from "./Components/InterviewRound";
import RichTextEditor from "components/Common/Editors/RichTextEditor";
import {
  compensationOptions,
  timeArray,
  timeZones,
  time_Zones
} from "pages/B2B/constants";
import {
  currencyList,
  employmentTypes,
  jobTypes,
} from "containers/HiringManager/Jobs.util";
import JobLocationSelector from "pages/B2B/Jobs/CreateJob/JobLocationSelector";
import TimeZoneSelector from "pages/B2B/Jobs/CreateJob/JobTimeZoneSelector";
import {
  getFormatLocationForSave,
  getLocationInOptionDataFormat,
} from "containers/HiringManager/Jobs.util";
import { createJob, getJobDetails, updateJob, PublishJob } from "./services";
import { OverlayLoader } from "components/Common/Loader/loaderWithOverlay";
import {
  Label,
  FormikStyledTextField,
} from "./Components/FormikStyledTextField";
import { toast } from "react-toastify";
import pdfToText from "react-pdftotext";
import { IAddJob, IClientInterviewProcess } from "./types";
import { OptionsDataType } from "types/Jobs";
import { useAddJobContext } from "./context/AddJobContext";

const JobDetails: React.FC<{ submitBtnRef: any, setSourcingType: (stype: string) => void }> = ({ submitBtnRef, setSourcingType }) => {
  const {
    jobId,
    expertId,
    sourcingType,
    setLoading,
    handleSetJobId,
    setJobTitle
  } = useAddJobContext();

  const isEdit = Boolean(jobId);
  const [trackId, setTrackId] = useState<string>("");
  const [description, setDescription] = useState("");
  const [interviewRounds, setInterviewRounds] = useState([{ id: 1 }]);
  const [JDLoading, setJDLoading] = useState(false);
  const [jobDescSource, setJobDescSource] = useState<"manual" | "upload">(
    "manual"
  );

  const defaultInitialValues = {
    jobTitle: "",
    openPositions: 0,
    jobType: "",
    employmentType: "",
    currency: "",
    minSalary: 0,
    maxSalary: 0,
    description: "",
    jobDescription: "",
    parsedPdfText: "",
    fileName: "",
    equity: "",
    selectedLocations: [] as OptionsDataType[],
    numberOfDaysOfficeEveryWeek: 0,
    timezone: "",
    startTime: "",
    endTime: "",
    interviewRounds: [{ title: "", questions: "", criteria: "" }],
  };

  const [initialValues, setInitialValues] = useState(defaultInitialValues);

    useEffect(() => {
      if (isEdit && jobId && expertId) {
        setJDLoading(true);
        getJobDetails({ jobId, expertId })
          .then((data) => {
          
            if (data.apiStatus === "SUCCESS") {
              const output = data.output;

              const mapped = {
                jobTitle: output.jobTitle || output.title || "",
                openPositions: output.openPositions || 0,
                jobType: output.jobType || "",
                employmentType: output.employmentType || "",
                currency: output.currency || "",
                minSalary: output.minSalary || 0,
                maxSalary: output.maxSalary || 0,
                description: output?.trackDetails?.jobDescription,
                jobDescription: output?.trackDetails?.jobDescription,
                parsedPdfText: "",
                fileName: "",
                equity: output.equity || "",
                selectedLocations:
                  getLocationInOptionDataFormat(output.locations) || [],
                numberOfDaysOfficeEveryWeek:
                  output.numberOfDaysOfficeEveryWeek || 0,
                timezone: output.timezone || "",
                startTime: output.startTime || "",
                endTime: output.endTime || "",
                interviewRounds: output.trackDetails?.clientInterviewProcess
                  ? output.trackDetails.clientInterviewProcess.map((round: any) => ({
                      title: round.title,
                      questions: round.question,
                      criteria: round.criteria || "",
                    }))
                  : [{ title: "", questions: "", criteria: "" }],
              };

              setInitialValues(mapped);
              setTrackId(output.trackId);
              setDescription(mapped.description || "");
              setInterviewRounds(
                mapped.interviewRounds.map((_: any, i: number) => ({ id: i + 1 }))
              );
              setJobTitle(mapped.jobTitle);

              setSourcingType(output.sourcingType);
            } else {
              toast.error("Failed to load job details: " + data.apiMessage);
            }
          })
          .catch(() => toast.error("Error fetching job details"))
          .finally(() => setJDLoading(false));
      }
    }, [jobId, expertId, isEdit]);

  const validationSchema = Yup.object({
    jobTitle: Yup.string().required("Job title is required"),
    jobDescription: Yup.mixed().test(
      "jobDescription-required",
      "Please provide either a job description or upload a PDF file.",
      function () {
        const { description, parsedPdfText } = this.parent;
        return Boolean(description?.trim() || parsedPdfText?.trim());
      }
    ),
    interviewRounds: Yup.array().of(
      Yup.object().shape({
        title: Yup.string().required("Interview title is required"),
        questions: Yup.string().nullable(),
        criteria: Yup.string().nullable(),
      })
    ),
  });

  const handleFileChange = async (
    file: any,
    setFieldValue: (key: string, val: string) => void
  ) => {
    if (file?.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }

    setFieldValue("fileName", file.name);
    setLoading(true);
    try {
      const text = await pdfToText(file);
      setFieldValue("parsedPdfText", text);
      setFieldValue("jobDescription", text);
      setJobDescSource("upload");
      setDescription(text);
    } catch {
      toast.error("Could not parse PDF.");
    } finally {
      setLoading(false);
    }
  };

  const addInterviewRound = () => {
    setInterviewRounds([
      ...interviewRounds,
      { id: interviewRounds.length + 1 },
    ]);
  };
  
  const removeInterviewRound = (id: number) => {
    if (interviewRounds.length > 1) {
      setInterviewRounds(interviewRounds.filter((round) => round.id !== id));
    }
  };

  if (JDLoading) {
    return (
      <Box
        sx={{
          width: "99%",
          padding: "20px",
          border: "1px solid lightblue",
          borderRadius: "9px",
          margin: "30px auto 0",
          background: "#fff",
        }}
      >
        <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
        Fetching Job Details...
      </Box>
    );
  }

  return (
    <Formik
      enableReinitialize
      validateOnBlur
      validateOnChange
      validateOnMount
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const clientInterviewProcess: IClientInterviewProcess[] = values.interviewRounds.map((round, index) => ({
          title: round.title,
          question: round.questions,
          criteria: round.criteria || "",
          round: index + 1,
        }));

        const locations = getFormatLocationForSave(values.selectedLocations);

        const formattedData: IAddJob = {
          async: false,
          currency: values.currency,
          timezone: values.timezone,
          employmentType: values.employmentType,
          equity: values.equity,
          jobAccess: "PRIVATE",
          jobTitle: values.jobTitle,
          jobType: values.jobType,
          locations: locations,
          selectedLocations: values.selectedLocations,
          maxSalary: values.maxSalary,
          minSalary: values.minSalary,
          openPositions: values.openPositions,
          selectionSteps: [],
          title: values.jobTitle,
          fileName: values.fileName || "",
          jobDescription: values.jobDescription || "",
          clientInterviewProcess,
          startTime: values.startTime,
          endTime: values.endTime,
          numberOfDaysOfficeEveryWeek: values.numberOfDaysOfficeEveryWeek || null,
          expertId,
          sourcingType,
        };

        setLoading(true);

        const promise = isEdit && jobId ? updateJob(jobId, trackId, formattedData) : createJob(formattedData);

        promise
          .then(async (res) => {
            setLoading(false);
            if (res && res.output && res.apiStatus === "SUCCESS") {
              const finalJobId = res.output?.jobEntityResponse?.jobId || jobId;

              if (sourcingType !== "SOURCE_TALENT"){
                try {
                  await PublishJob({
                    jobId: finalJobId,
                    expertId,
                    jobDraftState: "PUBLISHED",
                  });
                } catch (publishErr) {
                  console.error("Error publishing job:", publishErr);
                  toast.error("Job saved, but error occurred while publishing.");
                }
              }

              const nextStep = sourcingType === "SOURCE_TALENT" ? "aiChat" : "thankyou";
              handleSetJobId(finalJobId, nextStep);
            } else {
              toast.error(res.apiMessage || "Something went wrong while saving. Please try again!");
            }
          })
          .catch((err) => {
            setLoading(false);
            toast.error(err.message || "Something went wrong while saving. Please try again!");
          });
      }}
    >
      {({ values, setFieldValue }) => (
        <Form>
        <OverlayLoader loading={JDLoading} />
        <Box
          sx={{
            width: "99%",
            padding: "20px",
            border: "1px solid lightblue",
            borderRadius: "9px",
            marginLeft: "auto",
            marginRight: "auto",
            marginTop: "30px",
            background: "#fff",
          }}
        >
          <Box>
            <Typography fontWeight="bold" sx={{ mb: 2, fontSize: "16px" }}>
              Basic Job Details
            </Typography>
            <Box display="flex" gap={2} mt={2}>
              <Box sx={{ width: "100%" }}>
                <Label>Job Title</Label>
                <Field
                  component={FormikStyledTextField}
                  name="jobTitle"
                  variant="outlined"
                  fullWidth
                  size="small"
                  placeholder="Enter Job Title"
                />
              </Box>
              {sourcingType === "SOURCE_TALENT" && (
                <>
                  <Box sx={{ width: "48.5%" }}>
                    <Label>No. of Positions: </Label>
                    <Box>
                      <Field
                        component={FormikStyledTextField}
                        name="openPositions"
                        type="number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        placeholder="0"
                      />
                    </Box>
                  </Box>
                  <Box sx={{ width: "50%" }}>
                    <Label>Job Type</Label>
                    <Field
                      component={FormikStyledTextField}
                      name="jobType"
                      select
                      fullWidth
                      size="small"
                      placeholder="Select Job Type"
                    >
                      {jobTypes.map((type, idx) => (
                        <MenuItem value={type.key}>{type.value}</MenuItem>
                      ))}
                    </Field>
                  </Box>
                </>
              )}
              {sourcingType === "SOURCE_TALENT" &&
                values.jobType !== "REMOTE" && (
                  <Box sx={{ width: "70%" }}>
                    <Label>Job Location</Label>
                    <JobLocationSelector
                      disabled={false}
                      handleChangeLocation={setFieldValue}
                      keyName="selectedLocations"
                      selectedLocations={values.selectedLocations}
                    />
                  </Box>
                )}
              {(sourcingType === "SOURCE_TALENT" &&
                values.jobType === "HYBRID" && (
                  <Box sx={{ width: "30%" }}>
                    <Label>Employee must visit</Label>
                    <Field
                      component={FormikStyledTextField}
                      name="numberOfDaysOfficeEveryWeek"
                      type="number"
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="0"
                    />
                  </Box>
                )) ||
                null}
              {sourcingType === "SOURCE_TALENT" &&
                values.jobType === "REMOTE" && (
                  <>
                    <Box sx={{ width: "50%" }}>
                    <Label>Time Zone:</Label>
                    <TimeZoneSelector
                    disabled={false}
                    selectedTimezone={values.timezone}
                    handleChangeTimezone={setFieldValue}
                    keyName="timezone"
                  />
                  </Box>

                    <Box sx={{ width: "25%" }}>
                      <Label>Start Time:</Label>
                      <Field
                        component={FormikStyledTextField}
                        name="startTime"
                        select
                        variant="outlined"
                        fullWidth
                        size="small"
                      >
                        {timeArray.map((time, idx) => {
                          return (
                            <MenuItem key={time.key + idx} value={time.key}>
                              {time.label}
                            </MenuItem>
                          );
                        })}
                      </Field>
                    </Box>
                    <Box sx={{ width: "25%" }}>
                      <Label>End Time:</Label>
                      <Field
                        component={FormikStyledTextField}
                        name="endTime"
                        select
                        variant="outlined"
                        fullWidth
                        size="small"
                      >
                        {timeArray.map((time, idx) => {
                          return (
                            <MenuItem key={time.key + idx} value={time.key}>
                              {time.label}
                            </MenuItem>
                          );
                        })}
                      </Field>
                    </Box>
                  </>
                )}
            </Box>
            {sourcingType === "SOURCE_TALENT" && (
              <Box display="flex" gap={2} mt={2}>
                <Box sx={{ width: "100%" }}>
                  <Label>Employment Type</Label>
                  <Field
                    component={FormikStyledTextField}
                    name="employmentType"
                    select
                    fullWidth
                    size="small"
                    placeholder="Employment Type"
                  >
                    {employmentTypes.map((type, idx) => (
                      <MenuItem value={type.key}>{type.value}</MenuItem>
                    ))}
                  </Field>
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Label>Compensation Type</Label>
                  <Field
                    component={FormikStyledTextField}
                    name="equity"
                    select
                    fullWidth
                    size="small"
                    placeholder="Compensation Type"
                  >
                    {compensationOptions.map((type, idx) => (
                      <MenuItem value={type.key}>{type.label}</MenuItem>
                    ))}
                  </Field>
                </Box>

                <Box sx={{ width: "100%" }}>
                  <Label>Currency</Label>
                  <Field
                    component={FormikStyledTextField}
                    name="currency"
                    select
                    fullWidth
                    size="small"
                    placeholder="Currency"
                  >
                    {currencyList.map((currency, idx) => {
                      return (
                        <MenuItem
                          key={currency.code + idx}
                          value={currency.code}
                        >
                          {currency.code}
                        </MenuItem>
                      );
                    })}
                  </Field>
                </Box>

                {values.equity === "CASH_ANDOR_EQUITY" && (
                  <Box sx={{ width: "48.5%" }}>
                    <Label>Annual Salary</Label>
                    <Box display="flex" gap={2}>
                      <Field
                        component={FormikStyledTextField}
                        name="minSalary"
                        variant="outlined"
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="Min"
                      />
                      <Field
                        component={FormikStyledTextField}
                        name="maxSalary"
                        variant="outlined"
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="Max"
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Radio
                checked={jobDescSource === "manual"}
                onChange={() => {
                  setJobDescSource("manual");
                  setFieldValue("parsedPdfText", "");
                  setFieldValue("jobDescription", description);
                }}
                value="manual"
                color="primary"
              />
              <Typography
                variant="body1"
                fontSize="14px"
                fontWeight="500"
                color="#333333"
              >
                Job / Assessment Description
              </Typography>
            </Box>
            <Box
              sx={{
                mt: 1,
                border: "1px solid #D1D5DB",
                borderRadius: "8px",
                padding: "10px",
                background: "#FFFFFF",
                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                marginBottom: "15px",
              }}
            >
              <Field
                className="form-control"
                name="description"
                id="job-description"
                type="text"
              >
                {() => (
                  <RichTextEditor
                    value={values.description}
                    disabled={false}
                    onChange={(val: any) => {
                      setDescription(val);
                      setFieldValue("description", val);
                      setFieldValue("jobDescription", val);
                    }}
                    id={"job-description-rte"}
                    customStyles={{
                      height: "200px",
                      minHeight: "200px",
                      boxShadow: "none",
                      background: "white",
                      border: "none",
                      resize: "none",
                      padding: "10px",
                    }}
                    placeholder="Enter Job Description"
                  />
                )}
              </Field>

              {/* <RichTextEditor
                value={values.description}
                id="job-description"
                onChange={(val: any) => {
                  setDescription(val);
                  setFieldValue("description", val);
                  setFieldValue("jobDescription", val);
                }}
                customStyles={{
                  height: "200px",
                  minHeight: "200px",
                  boxShadow: "none",
                  background: "white",
                  border: "none",
                  resize: "none",
                  padding: "10px",
                }}
                disabled={false}
              /> */}
            </Box>
          </Box>

          <Box display="flex" alignItems="center">
            <Radio
              checked={jobDescSource === "upload"}
              onChange={() => setJobDescSource("upload")}
              color="primary"
            />
            <Typography
              variant="body1"
              fontSize="14px"
              fontWeight="500"
              color="#98A2B3"
              lineHeight={1.2}
            >
              Upload a file containing the Job Description
            </Typography>
          </Box>
          <Box>
            <FileUpload
              onFileChange={(file) => handleFileChange(file, setFieldValue)}
            />
            <Field type="hidden" name="parsedPdfText" />
            <Field type="hidden" name="fileName" />
          </Box>

          <Field type="hidden" name="jobDescription" />
          <Box
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography fontWeight="500" fontSize="16px" color="#171717">
              Client Interview Process (Add all interview rounds)
            </Typography>
            <Button
              onClick={addInterviewRound}
              sx={{
                textTransform: "none",
                borderColor: "#007BFF",
                color: "#007BFF",
                "&:hover": { background: "#007BFF", color: "#fff" },
                fontWeight: "700",
                fontSize: "16px",
              }}
            >
              + Add new round
            </Button>
          </Box>
          {interviewRounds.map((round, index) => (
            <InterviewRound
              key={round.id}
              roundNumber={index + 1}
              roundIndex={index}
              onDelete={() => removeInterviewRound(round.id)}
            />
          ))}
          {/* Submit Button */}
          <Button
            type="submit"
            ref={submitBtnRef}
            style={{ display: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Form>
      )}
    </Formik>
  );
};

export default JobDetails;